import { getLatestStatusEntry, sortStatusHistoryChronologically } from './statusRemarks';

export function extractUpdatedCandidateFromStatusResponse(responseData, candidateId) {
  if (!responseData || !candidateId) return null;

  if (responseData.candidate && responseData.candidate._id) {
    return normalizeCandidateRecord(responseData.candidate);
  }

  const candidates = responseData.mainDocument?.candidates;
  if (!Array.isArray(candidates)) return null;

  const matched = candidates.find((candidate) => String(candidate._id) === String(candidateId));
  return matched ? normalizeCandidateRecord(matched) : null;
}

export function normalizeCandidateRecord(candidate) {
  if (!candidate) return candidate;

  const statusHistory = Array.isArray(candidate.Status)
    ? candidate.Status.map((entry) => ({
      _id: entry?._id,
      Status: entry?.Status || entry?.status || '',
      Date: entry?.Date || entry?.date || null,
      Remark: entry?.Remark || entry?.remark || '',
      Remarks: Array.isArray(entry?.Remarks)
        ? entry.Remarks.map((remarkEntry) => ({
          text: remarkEntry?.text || remarkEntry?.Remark || remarkEntry?.remark || '',
          createdAt: remarkEntry?.createdAt || remarkEntry?.Date || remarkEntry?.date || null,
        })).filter((remarkEntry) => remarkEntry.text)
        : [],
    }))
    : [];

  const sortedHistory = sortStatusHistoryChronologically(statusHistory);
  const latestEntry = getLatestStatusEntry(sortedHistory);

  return {
    ...candidate,
    Status: sortedHistory,
    currentStatus: latestEntry?.Status || candidate.currentStatus || '',
    latestStatusEntryId: candidate.latestStatusEntryId
      || (latestEntry?._id ? String(latestEntry._id) : ''),
  };
}

export function normalizeCandidateList(candidates = []) {
  return (Array.isArray(candidates) ? candidates : []).map(normalizeCandidateRecord);
}

export function normalizeRecruiterCandidateGroups(groups = []) {
  return (Array.isArray(groups) ? groups : []).map((group) => ({
    ...group,
    candidates: normalizeCandidateList(group.candidates || []),
  }));
}

const TL_REQUIREMENT_CANDIDATE_KEYS = [
  'userActiveCandidatesDetails',
  'todayUserCandidates',
  'todayTeamCandidates',
  'totalUserCandidatesDetails',
  'totalTeamCandidatesDetails',
  'totalCandidatesDetails',
  'combinedTodayCandidates',
];

export function normalizeTlRequirementAssignment(row) {
  if (!row) return row;

  const next = { ...row };
  TL_REQUIREMENT_CANDIDATE_KEYS.forEach((key) => {
    if (Array.isArray(next[key])) {
      next[key] = normalizeCandidateList(next[key]);
    }
  });

  return next;
}

export function patchCandidateInTlRequirementRows(rows, candidateId, updatedCandidate) {
  if (!Array.isArray(rows) || !updatedCandidate) return rows;

  return rows.map((row) => {
    const next = { ...row };
    let changed = false;

    TL_REQUIREMENT_CANDIDATE_KEYS.forEach((key) => {
      if (!Array.isArray(next[key])) return;
      const patched = patchCandidateInList(next[key], candidateId, updatedCandidate);
      if (patched !== next[key]) {
        next[key] = patched;
        changed = true;
      }
    });

    return changed ? next : row;
  });
}

export async function fetchRequirementRecruiters(api, requirementId) {
  if (!requirementId) return [];
  const response = await api.get(`/api/recruiters/${requirementId}`);
  return response.data?.recruiters || [];
}

export function findCandidateInRecruitersData(recruitersData, candidateId) {
  if (!Array.isArray(recruitersData) || !candidateId) return null;

  for (const group of recruitersData) {
    const matched = (group.candidates || []).find(
      (candidate) => String(candidate._id) === String(candidateId),
    );
    if (matched) return matched;
  }

  return null;
}

export function patchCandidateInList(candidates, candidateId, updatedCandidate) {
  if (!Array.isArray(candidates) || !updatedCandidate) return candidates;

  const normalizedCandidate = normalizeCandidateRecord(updatedCandidate);

  return candidates.map((candidate) => (
    String(candidate._id) === String(candidateId)
      ? normalizeCandidateRecord({ ...candidate, ...normalizedCandidate })
      : candidate
  ));
}

export function patchCandidateInRecruitersData(recruitersData, candidateId, updatedCandidate) {
  if (!Array.isArray(recruitersData) || !updatedCandidate) return recruitersData;

  return recruitersData.map((item) => ({
    ...item,
    candidates: patchCandidateInList(item.candidates, candidateId, updatedCandidate),
  }));
}

export function applyCandidateStatusUpdate({
  responseData,
  candidateId,
  setRecruitersData,
  setProfilesModalMeta,
  setCandidateDetails,
  setRequirementRows,
  setSelectedCandidates,
}) {
  const updatedCandidate = extractUpdatedCandidateFromStatusResponse(responseData, candidateId);
  if (!updatedCandidate) return null;

  const normalizedCandidate = normalizeCandidateRecord(updatedCandidate);

  if (typeof setRecruitersData === 'function') {
    setRecruitersData((prev) => patchCandidateInRecruitersData(prev, candidateId, normalizedCandidate));
  }

  if (typeof setProfilesModalMeta === 'function') {
    setProfilesModalMeta((prev) => {
      if (!Array.isArray(prev?.scopedCandidates)) return prev;
      return {
        ...prev,
        scopedCandidates: patchCandidateInList(prev.scopedCandidates, candidateId, normalizedCandidate),
      };
    });
  }

  if (typeof setRequirementRows === 'function') {
    setRequirementRows((prev) => patchCandidateInTlRequirementRows(prev, candidateId, normalizedCandidate));
  }

  if (typeof setSelectedCandidates === 'function') {
    setSelectedCandidates((prev) => patchCandidateInList(prev, candidateId, normalizedCandidate));
  }

  if (typeof setCandidateDetails === 'function') {
    setCandidateDetails(normalizedCandidate);
  }

  return normalizedCandidate;
}
